/**
 * A sticky column taller than the viewport. It scrolls with the page until
 * its bottom comes into view, then sticks there; scrolling back up reverses
 * that until its top is back under the header. A column short enough to fit
 * the viewport just sticks at its CSS `top`.
 *
 * The CSS `top` is the upper bound and `--sticky-column-bottom` the gap kept
 * below the column once its bottom sticks. Desktop only, matching the CSS.
 */
class StickyColumn extends HTMLElement {
  connectedCallback() {
    this.mediaQuery = window.matchMedia(
      `(min-width: ${window.themeBreakpoints["tablet-wide"]}px)`,
    );
    this.mediaQuery.addEventListener("change", this._measure);
    window.addEventListener("resize", this._measure);
    window.addEventListener("scroll", this._onScroll, { passive: true });

    this.resizeObserver = new ResizeObserver(this._measure);
    this.resizeObserver.observe(this);

    // A colourway swap morphs this element in place, and morphdom drops the
    // inline `top` because the server markup has none.
    this.styleObserver = new MutationObserver(this._apply);
    this.styleObserver.observe(this, { attributeFilter: ["style"] });
  }

  disconnectedCallback() {
    this.mediaQuery.removeEventListener("change", this._measure);
    window.removeEventListener("resize", this._measure);
    window.removeEventListener("scroll", this._onScroll);
    this.resizeObserver.disconnect();
    this.styleObserver.disconnect();
  }

  _measure = () => {
    // Cleared to read the stylesheet's `top`, and restored in the same task,
    // so it never paints.
    this.style.removeProperty("top");

    if (!this.mediaQuery.matches) {
      this.top = null;
      return;
    }

    const styles = getComputedStyle(this);
    const bottomGap =
      parseFloat(styles.getPropertyValue("--sticky-column-bottom")) || 0;

    this.maxTop = parseFloat(styles.top) || 0;
    this.minTop = Math.min(
      this.maxTop,
      window.innerHeight - this.offsetHeight - bottomGap,
    );
    this.lastScrollY = window.scrollY;
    this.top = this._clamp(this.top ?? this.maxTop);
    this._apply();
  };

  // Whatever the column's sticky state, `top` tracks the scroll 1:1 within
  // its bounds, so it lines up with the column's natural position by the
  // time sticking takes over.
  _onScroll = () => {
    if (this.top === null || this.top === undefined) return;

    const delta = window.scrollY - this.lastScrollY;

    this.lastScrollY = window.scrollY;
    this.top = this._clamp(this.top - delta);
    this._apply();
  };

  _apply = () => {
    if (this.top === null || this.top === undefined) return;

    const top = `${this.top}px`;

    if (this.style.top !== top) this.style.top = top;
  };

  _clamp(top) {
    return Math.min(this.maxTop, Math.max(this.minTop, top));
  }
}

if (!customElements.get("sticky-column")) {
  customElements.define("sticky-column", StickyColumn);
}
